import type { Plan } from '@heraion/shared';

import { PickerImportList, PickerImportRow } from './PickerImportList';
import type { PickerStyles } from './pickerStyles';
import type { UiTheme } from '../theme';

// PE-40 — the Workout tab's body: the athlete's saved single workouts, one row
// each, tapped to drop their exercises into the day being authored.
//
// ⛔ NOT a multi-select list, unlike the Exercises tab beside it. Importing two
// workouts at once is two decisions the coach would have to hold in their head
// while the day silently doubles; one tap, one import, and the tab stays open
// for a second one if they want it.
//
// ⛔ Gated by DATA, not by a flag: the tab only exists where the host supplies
// `workouts` and `onAddWorkout`, so the Logger's own picker cannot grow it by
// accident — the same capability-by-omission rule hostDeps uses for its
// native-only slots.
//
// #336: the list, its hint, its empty state and the row are PickerImportList,
// shared with the Programs tab.
export function PickerPlanList({
  theme,
  styles,
  t,
  workouts,
  onPick,
  weeks,
}: {
  theme: UiTheme;
  styles: PickerStyles;
  t: (key: string, opts?: Record<string, unknown>) => string;
  workouts: ReadonlyArray<Plan>;
  onPick: (plan: Plan) => void;
  /** How many weeks the import will fill — the host's `cycleWeeks`. */
  weeks: number;
}) {
  return (
    <PickerImportList
      theme={theme}
      styles={styles}
      data={workouts}
      keyExtractor={(p) => p.id}
      // ⭐ The consequence is stated BEFORE the list, not after a tap: an
      // import that quietly fills six weeks is the kind of surprise the
      // remove-confirm of PE-44 had to be taught to avoid.
      // ⛔ …but only when there is something to tap. The visual bench caught
      // it sitting above the empty state, promising what an import WOULD do
      // to someone who has no workout to import: a caption explaining a
      // control that is not there.
      hint={t('composer.importPlanHint', {
        defaultValue:
          'Its exercises are added to the day you are editing, in every week of the cycle.',
      })}
      emptyTitle={t('composer.importPlanEmpty', {
        defaultValue: 'No saved plans yet.',
      })}
      renderItem={(item) => {
        const count = item.entries.length;
        const meta = t('composer.importPlanMeta', {
          count,
          defaultValue: '{{count}} exercises',
        });
        return (
          <PickerImportRow
            styles={styles}
            title={item.name}
            sub={meta}
            // ⛔ The row says what it WILL DO, not just what it is: a screen
            // reader reaching a list of names has no way to know a tap here
            // imports rather than opens.
            // ⛔ The label carries the REACH, not just the name. The header
            // above says "in every week of the cycle" once, at the top of a
            // scrolling list — someone arriving at this row by screen reader,
            // or after scrolling, never met it. The constraint belongs where
            // the tap is [[feedback_write_the_constraint_where_it_is_visible]].
            accessibilityLabel={t('composer.importPlanAction', {
              name: item.name,
              meta,
              count: weeks,
              defaultValue:
                'Add {{name}} to this day ({{meta}}), in all {{count}} weeks of the cycle',
            })}
            onPress={() => onPick(item)}
          />
        );
      }}
    />
  );
}
